import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {saveEntitySummary} from '../../../firebase/firestore/entityWriters';

/**
 * Inline editor for an entity's short description on KB / chart entity surfaces.
 */
export default function EntityDescriptionEditor({
    entityId,
    entity,
    canEdit = false,
    onSaved,
}) {
    const description = entity?.description || '';
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(description);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!editing) {
            setDraft(description);
        }
    }, [description, editing]);

    const handleCancel = useCallback(() => {
        setDraft(description);
        setEditing(false);
        setError(null);
    }, [description]);

    const handleSave = useCallback(async () => {
        if (!entityId || !canEdit) return;
        setSaving(true);
        setError(null);
        try {
            const nextDescription = draft.trim() === '' ? null : draft.trim();
            await saveEntitySummary(entityId, {
                ...entity,
                wikidataId: entity?.wikidataId || entityId,
                description: nextDescription,
            });
            setEditing(false);
            onSaved?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    }, [canEdit, draft, entity, entityId, onSaved]);

    if (!description && !canEdit) {
        return null;
    }

    if (editing) {
        return (
            <Stack spacing={1}>
                <TextField
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="Add a short description"
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                    disabled={saving}
                />
                {error ? (
                    <Alert severity="error">
                        {error.message || 'Failed to save description.'}
                    </Alert>
                ) : null}
                <Box sx={{display: 'flex', gap: 1}}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={14}/> : null}
                    >
                        Save
                    </Button>
                    <Button size="small" onClick={handleCancel} disabled={saving}>
                        Cancel
                    </Button>
                </Box>
            </Stack>
        );
    }

    return (
        <Stack spacing={0.75}>
            {description ? (
                <Typography variant="body1" color="text.secondary">{description}</Typography>
            ) : (
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No description yet.
                </Typography>
            )}
            {canEdit ? (
                <Button
                    size="small"
                    variant="text"
                    startIcon={<EditIcon fontSize="small"/>}
                    onClick={() => setEditing(true)}
                    sx={{alignSelf: 'flex-start', px: 0}}
                >
                    {description ? 'Edit description' : 'Add description'}
                </Button>
            ) : null}
        </Stack>
    );
}
